import React, { useState } from "react";
import { auth } from "./firebaseConfig";
import { signInWithPopup, GoogleAuthProvider, signInWithEmailAndPassword, sendPasswordResetEmail } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { db } from "./firebaseConfig";
import { useNavigate } from "react-router-dom";

// Initialize Google Auth Provider
const provider = new GoogleAuthProvider();

// ✅ Force Google to show account selection prompt
provider.setCustomParameters({
  prompt: "select_account",
});

const Login = ({ isProfilePage = false }) => {
  // Get the current user if we're on a profile page
  const currentUser = isProfilePage ? auth.currentUser : null;
  // Check if user is signed in with Google
  const isGoogleUser = currentUser && 
    currentUser.providerData && 
    currentUser.providerData.length > 0 && 
    currentUser.providerData[0].providerId === "google.com";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isHovered, setIsHovered] = useState(false);
  const [isGoogleHovered, setIsGoogleHovered] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetMessage, setResetMessage] = useState({ text: "", isError: false });
  const navigate = useNavigate();

  // Function to handle password reset
  const handlePasswordReset = async (e) => {
    e.preventDefault();
    console.log("Password reset requested for:", resetEmail);
    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setResetMessage({
        text: "If an account exists for that email, a reset link has been sent.",
        isError: false
      });
      // Clear the form after successful submission
      setResetEmail("");
    } catch (error) {
      console.error("Error sending password reset email:", error.message);
      setResetMessage({
        text: error.message,
        isError: true
      });
    }
  };

  // Function to handle email/password login
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      console.log("User signed in:", userCredential.user.email);
      setTimeout(() => navigate("/dashboard"), 3000);
    } catch (error) {
      console.error("Error logging in:", error.message);
      alert("Invalid email or password. Please try again.");
    }
  };

  // Function to handle Google login
  const handleGoogleSignIn = async () => {
    try {
      const result = await signInWithPopup(auth, provider);
      console.log("User signed in:", result.user.displayName, result.user.email);
      setTimeout(() => navigate("/dashboard"), 3000);
    } catch (error) {
      console.error("Error during Google Sign-In:", error.message);
    }
  };

  const pageStyle = {
    backgroundImage: `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url("/green1.jpg")`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    height: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
    fontFamily: "'Poppins', 'Segoe UI', Arial, sans-serif",
  };

  const formStyle = {
    backgroundColor: "rgba(255, 255, 255, 0.95)",
    padding: "35px",
    borderRadius: "16px",
    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.25)",
    textAlign: "center",
    width: "380px",
    animation: "fadeIn 1.2s ease-in-out",
    backdropFilter: "blur(10px)",
  };

  const logoContainerStyle = {
    display: "flex",
    justifyContent: "center",
    marginBottom: "24px",
  };

  const logoStyle = {
    width: "110px",
    filter: "drop-shadow(0 4px 6px rgba(45, 106, 79, 0.2))",
    transition: "transform 0.3s ease",
    transform: "scale(1)",
    animation: "pulse 2s infinite ease-in-out",
  };

  const titleStyle = {
    color: "#2d6a4f",
    marginBottom: "25px",
    fontSize: "22px",
    fontWeight: "600",
    lineHeight: "1.4",
    letterSpacing: "0.2px",
    textShadow: "0 1px 2px rgba(0,0,0,0.05)",
  };

  const inputContainerStyle = {
    position: "relative",
    marginBottom: "16px",
  };

  const inputStyle = {
    padding: "14px 16px",
    width: "100%",
    border: "1.5px solid #e0e0e0",
    borderRadius: "8px",
    fontSize: "15px",
    transition: "all 0.3s ease",
    boxSizing: "border-box",
    backgroundColor: "rgba(255, 255, 255, 0.8)",
    outline: "none",
  };

  const focusedInputStyle = {
    ...inputStyle,
    borderColor: "#2d6a4f",
    boxShadow: "0 0 0 3px rgba(45, 106, 79, 0.15)",
    backgroundColor: "#ffffff",
  };

  const buttonStyle = {
    backgroundColor: isHovered ? "#1e5739" : "#2d6a4f",
    color: "#FFFFFF",
    border: "none",
    borderRadius: "8px",
    padding: "14px 20px",
    marginTop: "16px",
    cursor: "pointer",
    width: "100%",
    fontSize: "16px",
    fontWeight: "600",
    transition: "all 0.3s ease",
    boxShadow: "0 4px 6px rgba(45, 106, 79, 0.2)",
    letterSpacing: "0.5px",
  };

  const googleButtonStyle = {
    backgroundColor: isGoogleHovered ? "#f5f5f5" : "#ffffff",
    color: "#444444",
    border: "1px solid #dedede",
    borderRadius: "8px",
    padding: "12px 20px",
    marginTop: "16px",
    cursor: "pointer",
    width: "100%",
    fontSize: "16px",
    fontWeight: "500",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.3s ease",
    boxShadow: isGoogleHovered ? "0 4px 10px rgba(70, 160, 97, 0.25)" : "0 2px 5px rgba(0,0,0,0.1)",
  };

  const googleLogoStyle = {
    width: "20px",
    marginRight: "12px",
  };

  const dividerStyle = {
    display: "flex",
    alignItems: "center",
    margin: "20px 0",
  };

  const lineStyle = {
    flex: "1",
    height: "1px",
    backgroundColor: "#e0e0e0",
  };

  const orTextStyle = {
    margin: "0 10px",
    color: "#757575",
    fontSize: "14px",
  };

  const signupLinkStyle = {
    marginTop: "20px",
    fontSize: "15px",
    color: "#2d6a4f",
    textAlign: "center",
    cursor: "pointer",
    fontWeight: "500",
  };

  const forgotPasswordLinkStyle = {
    fontSize: "14px",
    color: "#2d6a4f",
    textAlign: "right",
    cursor: "pointer",
    marginTop: "8px",
    marginBottom: "16px",
    display: "block",
  };

  const resetFormStyle = {
    marginTop: "16px",
    padding: "16px",
    backgroundColor: "rgba(240, 248, 244, 0.6)",
    borderRadius: "8px",
    border: "1px solid #e0e0e0",
  };

  const resetMessageStyle = {
    padding: "10px",
    marginTop: "10px",
    borderRadius: "6px",
    fontSize: "14px",
    textAlign: "center",
  };

  const successMessageStyle = {
    ...resetMessageStyle,
    backgroundColor: "rgba(45, 106, 79, 0.1)",
    color: "#2d6a4f",
    border: "1px solid rgba(45, 106, 79, 0.2)",
  };

  const errorMessageStyle = {
    ...resetMessageStyle,
    backgroundColor: "rgba(220, 53, 69, 0.1)",
    color: "#dc3545",
    border: "1px solid rgba(220, 53, 69, 0.2)",
  };

  const footerStyle = {
    marginTop: "30px",
    fontSize: "13px",
    color: "rgba(255, 255, 255, 0.9)",
    textAlign: "center",
    letterSpacing: "0.5px",
    textShadow: "0 1px 2px rgba(0,0,0,0.3)",
  };

  return (
    <div style={pageStyle}>
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(-20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes pulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.05); }
            100% { transform: scale(1); }
          }
        `}
      </style>
      <div style={formStyle}>
        <div style={logoContainerStyle}>
          <img src="/newlogo.png" alt="The Logo" style={logoStyle} />
        </div>
         <h1 style={titleStyle}>Welcome to <br></br><strong>AI AutoMarker</strong><br></br> <br></br> <em>Transforming Assessment & Grading</em></h1>
        <form onSubmit={handleSubmit}>
          <div style={inputContainerStyle}>
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={inputStyle}
              required
            />
          </div>
          <div style={inputContainerStyle}>
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={inputStyle}
              required
            />
          </div>
          {/* Only show the forgot password link if not on profile page OR if on profile page but not a Google user */}
          {(!isProfilePage || (isProfilePage && !isGoogleUser)) && (
            <div>
              <span 
                style={forgotPasswordLinkStyle} 
                onClick={() => setShowForgotPassword(!showForgotPassword)}
              >
                Forgot your password?
              </span>
            </div>
          )}
          <button 
            type="submit" 
            style={buttonStyle}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            Sign In
          </button>
        </form>
        
        {/* Password reset form - moved outside the main login form to prevent nesting issues */}
        {showForgotPassword && (
          <div style={resetFormStyle}>
            <form onSubmit={handlePasswordReset}>
              <div style={inputContainerStyle}>
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  style={inputStyle}
                  required
                />
              </div>
              <button 
                type="submit" 
                style={{
                  ...buttonStyle,
                  marginTop: "10px",
                  padding: "10px 16px",
                  fontSize: "14px"
                }}
              >
                Send Reset Link
              </button>
            </form>
            {resetMessage.text && (
              <div style={resetMessage.isError ? errorMessageStyle : successMessageStyle}>
                {resetMessage.text}
              </div>
            )}
          </div>
        )}
        
        <div style={dividerStyle}>
          <div style={lineStyle}></div>
          <span style={orTextStyle}>OR</span>
          <div style={lineStyle}></div>
        </div>
        
        <button
          onClick={handleGoogleSignIn}
          style={googleButtonStyle}
          onMouseEnter={() => setIsGoogleHovered(true)}
          onMouseLeave={() => setIsGoogleHovered(false)}
        >
          <img
            src="https://developers.google.com/identity/images/g-logo.png"
            alt="Google logo"
            style={googleLogoStyle}
          />
          Sign in with Google
        </button>
        
        <p
          style={signupLinkStyle}
          onClick={() => navigate("/signup")}
        >
          Don't have an account? <span style={{textDecoration: "underline"}}>Sign Up</span>
        </p>
      </div>
      <p style={footerStyle}>Created by Ryan Higgins</p>
    </div>
  );
};

export default Login;
